// FEN 解析/序列化
// 格式: <rank9>/<rank8>/.../<rank0> <turn> <placeholder> <placeholder> <halfmove> <fullmove>
// 每行: 从 file 0 到 file 8, 大写=红, 小写=黑, 数字表示连续空格
// 标准: 第一行 = rank 9 (黑方背排); 最后一行 = rank 0 (红方背排)

import { Color, GameState, Piece, PieceType, PIECE_LETTERS, START_FEN, makeSquare } from './types';

const PARSER: Record<string, PieceType> = {
  K: 'K', A: 'A', B: 'B', N: 'N', R: 'R', C: 'C', P: 'P',
};

export function parseFen(fen: string): GameState {
  const parts = fen.trim().split(/\s+/);
  if (parts.length < 4) throw new Error(`Invalid FEN (need at least 4 fields): ${fen}`);

  const [rankPart, turnRaw, , , halfRaw = '0', fullRaw = '1'] = parts;
  const turn: Color = turnRaw === 'r' ? 'r' : 'b';

  const rows = rankPart.split('/');
  if (rows.length !== 10) throw new Error(`FEN needs 10 rows, got ${rows.length}`);

  const board: (Piece | null)[] = new Array(90).fill(null);
  // rows[0] = rank 9, rows[9] = rank 0
  for (let i = 0; i < 10; i++) {
    const rank = 9 - i;
    const row = rows[i];
    let file = 0;
    for (const ch of row) {
      if (/[1-9]/.test(ch)) {
        file += parseInt(ch, 10);
      } else {
        const upper = ch.toUpperCase();
        if (!(upper in PARSER)) throw new Error(`Bad FEN char: ${ch}`);
        const type = PARSER[upper];
        const color: Color = upper === ch ? 'r' : 'b';
        if (file >= 9) throw new Error(`FEN row ${i} too long`);
        board[makeSquare(file, rank)] = { color, type };
        file += 1;
      }
    }
    if (file !== 9) throw new Error(`FEN row ${i} has ${file} cells (need 9)`);
  }

  return {
    board,
    turn,
    halfmove: parseInt(halfRaw, 10) || 0,
    fullmove: parseInt(fullRaw, 10) || 1,
  };
}

export function toFen(state: GameState): string {
  const rows: string[] = [];
  for (let rank = 9; rank >= 0; rank--) {
    let row = '';
    let empty = 0;
    for (let file = 0; file < 9; file++) {
      const p = state.board[makeSquare(file, rank)];
      if (!p) {
        empty += 1;
      } else {
        if (empty > 0) { row += String(empty); empty = 0; }
        const letter = PIECE_LETTERS[p.type];
        row += p.color === 'r' ? letter.toUpperCase() : letter.toLowerCase();
      }
    }
    if (empty > 0) row += String(empty);
    rows.push(row);
  }
  // 占位符字段保持 '-' (本游戏不使用回合计数特殊状态)
  return `${rows.join('/')} ${state.turn} - - ${state.halfmove} ${state.fullmove}`;
}

export function startState(): GameState { return parseFen(START_FEN); }
